import React, { useRef, useState } from 'react';
import classnames from 'classnames';
import ReactDOM from 'react-dom';
import { motion } from 'framer-motion';
import { u as useSafeLayoutEffect_1 } from './useSafeLayoutEffect-7WC_06jN.js';
import { u as useIsMounted_2 } from './useIsMounted-Bhu6Lxq6.js';
import { usePopper } from 'react-popper';

var styles = {"shadowActivator":"q07bkGoq714-","tooltipWrapper":"dkCTRfc3vYk-","tooltip":"yAFP4gRKlTM-","arrow":"j9KgRON7iTU-","top":"DpxWsz9QuIo-","bottom":"_5RblY1rnJOA-","left":"C9MXsqd4wLM-","right":"_2ZZcgQ4fJ-k-","tooltipMessage":"TpdeLUuiQMg-"};

const DEFAULT_PLACEMENT = "top";
function useTooltipPositioning({ preferredPlacement, }) {
    var _a, _b;
    const shadowRef = useRef(null);
    const [positionElement, setTooltipRef] = useState();
    const [arrowElement, setArrowRef] = useState();
    const popper = usePopper((_a = shadowRef.current) === null || _a === void 0 ? void 0 : _a.nextElementSibling, positionElement, {
        placement: preferredPlacement,
        modifiers: [
            { name: "flip", options: { fallbackPlacements: ["bottom"] } },
            {
                name: "arrow",
                options: { element: arrowElement },
            },
        ],
    });
    return Object.assign(Object.assign({}, popper), { placement: ((_b = popper.state) === null || _b === void 0 ? void 0 : _b.placement) || DEFAULT_PLACEMENT, shadowRef,
        setArrowRef,
        setTooltipRef });
}

const variation = {
    startOrStop: { scale: 0.6, opacity: 0 },
    done: { scale: 1, opacity: 1 },
};
function Tooltip({ message, children, preferredPlacement = "top", }) {
    const [show, setShow] = useState(false);
    const { attributes, placement, shadowRef, styles: popperStyles, setArrowRef, setTooltipRef, } = useTooltipPositioning({ preferredPlacement: preferredPlacement });
    initializeListeners();
    const toolTipClassNames = classnames(styles.tooltipWrapper, placement === "bottom" && styles.bottom, placement === "top" && styles.top, placement === "left" && styles.left, placement === "right" && styles.right);
    return (React.createElement(React.Fragment, null,
        React.createElement("span", { className: styles.shadowActivator, ref: shadowRef }),
        children,
        React.createElement(TooltipPortal, null, show && Boolean(message) && (React.createElement("div", Object.assign({ className: toolTipClassNames, style: popperStyles.popper, ref: setTooltipRef, role: "tooltip" }, attributes.popper),
            React.createElement(motion.div, { className: styles.tooltip, variants: variation, initial: "startOrStop", animate: "done", exit: "startOrStop", transition: {
                    damping: 50,
                    stiffness: 500,
                } },
                React.createElement("p", { className: styles.tooltipMessage }, message),
                React.createElement("div", { ref: setArrowRef, style: popperStyles.arrow, className: styles.arrow })))))));
    function initializeListeners() {
        const showTooltip = () => {
            setShow(true);
        };
        const hideTooltip = () => {
            setShow(false);
        };
        const injectAttributes = () => {
            var _a;
            if ((_a = shadowRef === null || shadowRef === void 0 ? void 0 : shadowRef.current) === null || _a === void 0 ? void 0 : _a.nextElementSibling) {
                const activator = shadowRef.current.nextElementSibling;
                // Manually inject "aria-description" and "tabindex" to let the screen
                // readers read the tooltip message.
                // This is to avoid having to add those attribute as a prop on every
                // component we have.
                activator.setAttribute("aria-description", message);
                activator.setAttribute("tabindex", "0"); // enable focus
            }
        };
        const addListeners = () => {
            var _a;
            if ((_a = shadowRef === null || shadowRef === void 0 ? void 0 : shadowRef.current) === null || _a === void 0 ? void 0 : _a.nextElementSibling) {
                const activator = shadowRef.current.nextElementSibling;
                activator.addEventListener("mouseenter", showTooltip);
                activator.addEventListener("mouseleave", hideTooltip);
                activator.addEventListener("focus", showTooltip);
                activator.addEventListener("blur", hideTooltip);
            }
        };
        const removeListeners = () => {
            var _a;
            if ((_a = shadowRef === null || shadowRef === void 0 ? void 0 : shadowRef.current) === null || _a === void 0 ? void 0 : _a.nextElementSibling) {
                const activator = shadowRef.current.nextElementSibling;
                activator.removeEventListener("mouseenter", showTooltip);
                activator.removeEventListener("mouseleave", hideTooltip);
                activator.removeEventListener("focus", showTooltip);
                activator.removeEventListener("blur", hideTooltip);
            }
        };
        useSafeLayoutEffect_1(() => {
            injectAttributes();
            addListeners();
            return () => {
                removeListeners();
            };
        }, []);
    }
}
function TooltipPortal({ children }) {
    const mounted = useIsMounted_2();
    if (!(mounted === null || mounted === void 0 ? void 0 : mounted.current)) {
        return null;
    }
    return ReactDOM.createPortal(children, document.body);
}

export { Tooltip as T };
